import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, signal, computed, effect, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TreeTableModule } from 'primeng/treetable';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { MenuItem, TreeNode } from 'primeng/api';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { buildLocationTree } from '@/cloud/models/tree.models';
import { TooltipModule } from 'primeng/tooltip';
import { LocationPickerComponent } from '@/cloud/components/location-picker/location-picker.component';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';

@Component({
    selector: 'app-location-tree',
    standalone: true,
    imports: [CommonModule, TreeTableModule, ContextMenuModule, DialogModule, InputTextModule, ButtonModule, SelectModule, FormsModule, RouterModule, LocationPickerComponent, IconFieldModule, InputIconModule, EmptyStateComponent, TooltipModule],
    templateUrl: './location-tree.component.html',
    styleUrls: ['./location-tree.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationTreeComponent {
    // input signals for reactive updates from parent
    namespaceId = input<string | null>(null, { alias: 'namespaceId' });
    locationId = input<string | null>(null, { alias: 'locationId' });
    @Output() selectionChange = new EventEmitter<TreeNode | null>();

    constructor(
        private cloud: CloudApiService,
        private router: Router
    ) {
        this.initEffects();
    }

    // expansion & tree signals
    expandedKeySet = signal<Set<string>>(new Set());
    pendingScrollKey = signal<string | null>(null);
    filterText = signal('');
    selectedNode = signal<TreeNode | null>(null);
    contextMenuTarget = signal<TreeNode | null>(null);
    contextMenuItems = signal<MenuItem[]>([]);

    // Create dialog state
    showCreateDialog = signal(false);
    createName = signal('');
    createCode = signal('');
    createType = signal<string | null>(null);
    creatingParent = signal<TreeNode | null>(null);
    createSubmitting = signal(false);
    createError = signal<string | null>(null);
    locationTypeOptions = [
        { label: 'Room', value: 'room' },
        { label: 'Floor', value: 'floor' },
        { label: 'Building', value: 'building' },
        { label: 'Rack', value: 'rack' }
    ];

    // Delete dialog state
    showDeleteDialog = signal(false);
    deleteSubmitting = signal(false);
    deleteError = signal<string | null>(null);
    deleteTarget = signal<TreeNode | null>(null);

    // Rename dialog state
    showRenameDialog = signal(false);
    renameTarget = signal<TreeNode | null>(null);
    renameName = signal('');
    renameCode = signal('');
    renameType = signal<string | null>(null);
    renameSubmitting = signal(false);
    renameError = signal<string | null>(null);

    // Move location dialog state
    showMoveLocationDialog = signal(false);
    moveLocationTarget = signal<TreeNode | null>(null);
    moveLocationNewParentId = signal<string | null>(null);
    moveToRoot = signal(false);
    moveLocationSubmitting = signal(false);
    moveLocationError = signal<string | null>(null);

    // Drag and drop state
    draggedNode = signal<TreeNode | null>(null);
    dropTargetNode = signal<TreeNode | null>(null);
    isDragging = signal(false);
    handleMouseDownNode = signal<TreeNode | null>(null); // Track which node's handle was pressed

    // Device creation dialog state
    showCreateDeviceDialog = signal(false);
    createDeviceName = signal('');
    createDeviceSubmitting = signal(false);
    createDeviceError = signal<string | null>(null);
    createDeviceResult = signal<{ id: string; registrationCode: string; registrationExpiresUtc?: string | null } | null>(null);
    createDeviceLocationTarget = signal<TreeNode | null>(null);
    createDeviceLocationId = signal<string | null>(null); // selected location id from dropdown
    createDeviceLocationOptions = computed(() => {
        // Flatten locations within current namespace for selection
        const nsId = this.namespaceId();
        const locs = (this.locations() || []).filter((l) => !nsId || l.appNamespaceId === nsId);
        return locs.map((l) => ({ label: l.name, value: l.id }));
    });

    // Utility: compute relative expiry
    relativeExpiry(expiryIso: string | null | undefined): string {
        if (!expiryIso) return 'unknown';
        const exp = new Date(expiryIso);
        const now = new Date();
        let diffMs = exp.getTime() - now.getTime();
        if (diffMs <= 0) return 'expired';
        const minutes = Math.floor(diffMs / 60000);
        if (minutes < 1) {
            const seconds = Math.floor(diffMs / 1000);
            return seconds + 's';
        }
        if (minutes < 60) return minutes + ' minute' + (minutes === 1 ? '' : 's');
        const hours = Math.floor(minutes / 60);
        const remMin = minutes % 60;
        return hours + 'h ' + (remMin ? remMin + 'm' : '');
    }

    copyCode(code: string | null | undefined) {
        if (!code) return;
        try {
            navigator.clipboard?.writeText(code);
        } catch {
            /* ignore */
        }
    }

    get namespaces() {
        return this.cloud.namespaces;
    }
    get locations() {
        return this.cloud.locations;
    }

    /** Locations of the selected tenant only; the tenant itself is chosen in the topbar. */
    treeNodes = computed<TreeNode[]>(() => {
        return buildLocationTree(this.locations(), this.namespaceId()).map((root) => this.cloneWithExpansion(root));
    });

    /** Writer/Admin on the selected tenant may add a top-level location. */
    canCreateRoot = computed(() => {
        const ns = this.namespaceId();
        return !!ns && this.cloud.hasNamespaceRole(ns, [1, 2]);
    });

    /** Add a top-level location to the selected tenant (replaces the old tenant-root context menu item). */
    openCreateRoot() {
        if (!this.namespaceId()) return;
        this.creatingParent.set(null);
        this.createName.set('');
        this.createCode.set('');
        this.createType.set(null);
        this.createError.set(null);
        this.showCreateDialog.set(true);
    }

    filteredNodes = computed<TreeNode[]>(() => {
        const term = this.filterText().trim().toLowerCase();
        const nodes = this.treeNodes();
        if (!term) return nodes;
        const matchRecursive = (node: TreeNode): TreeNode | null => {
            const label = (node.label || '').toString().toLowerCase();
            const childMatches = (node.children || []).map((c) => matchRecursive(c)).filter(Boolean) as TreeNode[];
            if (label.includes(term) || childMatches.length) {
                // Expand every surviving branch so matches deep in the tree are visible at once.
                return { ...node, children: childMatches, leaf: childMatches.length === 0, expanded: true };
            }
            return null;
        };
        return nodes.map((n) => matchRecursive(n)).filter(Boolean) as TreeNode[];
    });

    private cloneWithExpansion(node: TreeNode): TreeNode {
        const children = (node.children || []).map((c) => this.cloneWithExpansion(c));
        return { ...node, children, expanded: node.key ? this.expandedKeySet().has(node.key) : false } as TreeNode;
    }

    private initEffects() {
        // Scroll effect
        effect(() => {
            const key = this.pendingScrollKey();
            if (!key) return;
            const nodes = this.treeNodes();
            const hasNode = nodes.some((ns) => {
                const find = (n: TreeNode): boolean => n.key === key || (n.children || []).some(find);
                return find(ns);
            });
            if (!hasNode) return;
            const container = document.querySelector('.tree-container') as HTMLElement | null;
            if (!container) return;
            const scrollTo = () => {
                const row = document.getElementById('tree-node-' + key);
                if (!row) return false;
                const rowRect = row.getBoundingClientRect();
                const cRect = container.getBoundingClientRect();
                const offset = rowRect.top - cRect.top + container.scrollTop;
                const target = offset - container.clientHeight / 2 + rowRect.height / 2;
                container.scrollTo({ top: Math.max(target, 0), behavior: 'smooth' });
                row.classList.add('highlight-pulse');
                setTimeout(() => row.classList.remove('highlight-pulse'), 1800);
                return true;
            };
            let attempts = 0;
            const maxAttempts = 10;
            const attemptScroll = () => {
                attempts++;
                if (scrollTo() || attempts >= maxAttempts) {
                    this.pendingScrollKey.set(null);
                    observer?.disconnect();
                    return;
                }
                setTimeout(attemptScroll, 90);
            };
            setTimeout(attemptScroll, 50);
            const observer = new MutationObserver(() => {
                scrollTo();
            });
            observer.observe(container, { childList: true, subtree: true });
        });

        // Context menu items effect (permission-aware)
        effect(() => {
            const target = this.contextMenuTarget();
            if (!target) {
                this.contextMenuItems.set([]);
                return;
            }
            const items: MenuItem[] = [];

            // Every node is a location of the selected tenant; its tenant id is on the data.
            const namespaceId = (target.data as any)?.appNamespaceId ?? this.namespaceId();

            // Helper role arrays: Reader=0, Writer=1, Admin=2
            const readerRoles = [0, 1, 2];
            const writerRoles = [1, 2];
            const adminRoles = [2];

            {
                // Open & Copy ID require at least Reader
                if (this.cloud.hasNamespaceRole(namespaceId, readerRoles)) items.push({ label: 'Open', icon: 'pi pi-folder-open', command: () => this.emitSelection(target) });
                if (this.cloud.hasNamespaceRole(namespaceId, readerRoles)) items.push({ label: 'Copy ID', icon: 'pi pi-copy', command: () => navigator.clipboard?.writeText((target.data as any).id) });

                if (this.cloud.hasNamespaceRole(namespaceId, writerRoles)) items.push({ separator: true });
                // New Sub-location and New Device require Writer or Admin
                if (this.cloud.hasNamespaceRole(namespaceId, writerRoles)) items.push({ label: 'New Sub-location', icon: 'pi pi-plus', command: () => this.openCreate(target) });
                if (this.cloud.hasNamespaceRole(namespaceId, writerRoles)) items.push({ label: 'New Device', icon: 'pi pi-microchip', command: () => this.openCreateDevice(target) });
                if (this.cloud.hasNamespaceRole(namespaceId, writerRoles)) items.push({ label: 'Update', icon: 'pi pi-cloud-upload', command: () => this.openDeployForLocation(target, namespaceId) });

                // Rename, Move & Delete generally require Admin
                if (this.cloud.hasNamespaceRole(namespaceId, adminRoles)) items.push({ label: 'Rename', icon: 'pi pi-pencil', command: () => this.openRename(target) });
                if (this.cloud.hasNamespaceRole(namespaceId, adminRoles)) items.push({ label: 'Move Location', icon: 'pi pi-arrow-right', command: () => this.openMoveLocation(target) });
                if (this.cloud.hasNamespaceRole(namespaceId, adminRoles)) items.push({ label: 'Delete', icon: 'pi pi-trash', command: () => this.openDelete(target) });
            }
            this.contextMenuItems.set(items);
        });

        // React to input changes for auto-select expansion
        effect(() => {
            const ns = this.namespaceId();
            const loc = this.locationId();
            if (!ns) return;
            queueMicrotask(() => this.trySelect(loc));
        });
    }

    emitSelection(node: TreeNode | null) {
        this.selectedNode.set(node);
        this.selectionChange.emit(node);
    }

    get expandedKeysMap(): { [key: string]: boolean } {
        const obj: Record<string, boolean> = {};
        for (const k of this.expandedKeySet()) obj[k] = true;
        return obj;
    }

    onExpand(node: TreeNode) {
        const next = new Set(this.expandedKeySet());
        if (node.key) next.add(node.key);
        this.expandedKeySet.set(next);
    }
    onCollapse(node: TreeNode) {
        const next = new Set(this.expandedKeySet());
        if (node.key) next.delete(node.key);
        this.expandedKeySet.set(next);
    }
    onSelect(node: TreeNode | undefined | null) {
        this.emitSelection(node ?? null);
    }

    // Selection & expansion path logic. A null location means "the tenant as a whole" (no node selected).
    private trySelect(locationId: string | null, attempt = 0) {
        if (!locationId) {
            this.emitSelection(null);
            return;
        }
        const nodes = this.treeNodes();
        if (!nodes || nodes.length === 0) {
            // Locations may still be loading; give up after ~5 s so a deleted id does not retry forever.
            if (attempt < 25) setTimeout(() => this.trySelect(locationId, attempt + 1), 200);
            return;
        }
        const findPath = (node: TreeNode, targetKey: string, acc: TreeNode[]): TreeNode[] | null => {
            const nextAcc = [...acc, node];
            if (node.key === targetKey) return nextAcc;
            for (const child of node.children || []) {
                const p = findPath(child, targetKey, nextAcc);
                if (p) return p;
            }
            return null;
        };
        let path: TreeNode[] | null = null;
        for (const root of nodes) {
            path = findPath(root, locationId, []);
            if (path) break;
        }
        if (!path) {
            if (attempt < 25) setTimeout(() => this.trySelect(locationId, attempt + 1), 200);
            return;
        }
        const current = new Set(this.expandedKeySet());
        path.forEach((p) => {
            if (p.key) current.add(p.key);
        });
        this.expandedKeySet.set(current);
        const target = path[path.length - 1];
        this.emitSelection(target);
        this.pendingScrollKey.set(target.key!);
    }

    // Context menu actions
    onRightClick(event: MouseEvent, node: TreeNode, menu: any) {
        event.preventDefault();
        this.contextMenuTarget.set(node);
        if (menu && typeof menu.show === 'function') {
            const synthetic = { ...event, pageX: event.pageX, pageY: event.pageY } as any;
            menu.show(synthetic);
        }
    }

    openDeployForLocation(node: TreeNode, namespaceId: string): void {
        const locationId = (node.data as any)?.id;
        if (!locationId) return;
        this.router.navigate(['/packages'], {
            queryParams: { ns: namespaceId, preSelectLocation: locationId, locationName: node.label }
        });
    }

    openCreate(parent: TreeNode) {
        this.creatingParent.set(parent);
        this.createName.set('');
        this.createCode.set('');
        this.createType.set(null);
        this.createError.set(null);
        this.showCreateDialog.set(true);
    }
    openDelete(node: TreeNode) {
        this.deleteTarget.set(node);
        this.deleteError.set(null);
        this.showDeleteDialog.set(true);
    }
    openRename(node: TreeNode) {
        this.renameTarget.set(node);
        const data: any = node.data || {};
        this.renameName.set(data.name || node.label || '');
        this.renameCode.set(data.code || '');
        this.renameType.set(data.type || null);
        this.renameError.set(null);
        this.showRenameDialog.set(true);
    }
    openMoveLocation(node: TreeNode) {
        this.moveLocationTarget.set(node);
        this.moveLocationNewParentId.set(null);
        this.moveLocationError.set(null);
        // Check if current location is already at root (parentId is null)
        const locationData: any = node.data;
        this.moveToRoot.set(locationData?.parentId == null);
        this.showMoveLocationDialog.set(true);
    }

    // Create
    confirmCreate() {
        if (this.createSubmitting()) return;
        const parent = this.creatingParent();
        const name = this.createName().trim();
        const code = this.createCode().trim();
        const type = this.createType();
        const namespaceId: string | null = parent ? (parent.data as any)?.appNamespaceId : this.namespaceId();
        if (!name || !namespaceId) {
            this.showCreateDialog.set(false);
            return;
        }
        const parentId = parent?.key ?? null;
        if (code) {
            const existing = (this.locations() || []).some((l) => l.appNamespaceId === namespaceId && l.code?.toLowerCase() === code.toLowerCase());
            if (existing) {
                this.createError.set('Code already in use in this tenant');
                return;
            }
        }
        this.createSubmitting.set(true);
        this.cloud
            .createLocation(namespaceId, parentId, name, code || undefined, type || undefined)
            .then((res) => {
                this.cloud.refreshNamespaceLocations(namespaceId);
                this.showCreateDialog.set(false);
                this.createSubmitting.set(false);
                const newId = res?.id;
                if (newId) setTimeout(() => this.trySelect(newId), 600);
            })
            .catch((err) => {
                const msg = err?.error || err?.message || 'Failed to create location';
                if (msg?.toLowerCase().includes('duplicate') || msg?.toLowerCase().includes('exists')) this.createError.set('A location with this code or name already exists');
                else this.createError.set(msg);
                this.createSubmitting.set(false);
            });
    }
    cancelCreate() {
        this.showCreateDialog.set(false);
    }

    // Delete
    confirmDelete() {
        if (this.deleteSubmitting()) return;
        const node = this.deleteTarget();
        if (!node || !node.data) {
            this.showDeleteDialog.set(false);
            return;
        }
        const locationId = (node.data as any).id;
        const namespaceId = (node.data as any).appNamespaceId;
        if (!locationId) {
            this.showDeleteDialog.set(false);
            return;
        }
        this.deleteSubmitting.set(true);
        this.deleteError.set(null);
        this.cloud
            .deleteLocation(locationId)
            .then(() => {
                if (namespaceId) this.cloud.refreshNamespaceLocations(namespaceId);
                const newSet = new Set(this.expandedKeySet());
                newSet.delete(node.key as string);
                this.expandedKeySet.set(newSet);
                const parentKey = (node.parent as any)?.key as string | undefined;
                this.showDeleteDialog.set(false);
                this.deleteSubmitting.set(false);
                if (parentKey) setTimeout(() => this.trySelect(parentKey), 500);
                else this.emitSelection(null);
            })
            .catch((err) => {
                const msg = err?.error || err?.message || 'Failed to delete location';
                this.deleteError.set(typeof msg === 'string' ? msg : 'Failed to delete location');
                this.deleteSubmitting.set(false);
            });
    }
    cancelDelete() {
        this.showDeleteDialog.set(false);
    }

    // Rename
    confirmRename() {
        if (this.renameSubmitting()) return;
        const node = this.renameTarget();
        if (!node || !node.data) {
            this.showRenameDialog.set(false);
            return;
        }
        const locationId = (node.data as any).id;
        const namespaceId = (node.data as any).appNamespaceId;
        const newName = this.renameName().trim();
        const newCodeRaw = this.renameCode().trim();
        const newType = this.renameType();
        if (!locationId || !newName) {
            this.showRenameDialog.set(false);
            return;
        }
        if (newCodeRaw) {
            const currentLocs = this.locations() || [];
            const duplicate = currentLocs.some((l) => l.appNamespaceId === namespaceId && l.code?.toLowerCase() === newCodeRaw.toLowerCase() && l.id !== locationId);
            if (duplicate) {
                this.renameError.set('Code already in use in this tenant');
                return;
            }
        }
        this.renameSubmitting.set(true);
        this.renameError.set(null);
        this.cloud
            .updateLocation(locationId, newName, newCodeRaw || undefined, newType || undefined)
            .then(() => {
                if (namespaceId) this.cloud.refreshNamespaceLocations(namespaceId);
                this.showRenameDialog.set(false);
                this.renameSubmitting.set(false);
                setTimeout(() => this.trySelect(locationId), 500);
            })
            .catch((err) => {
                const msg = err?.error || err?.message || 'Failed to update location';
                if (typeof msg === 'string' && (msg.toLowerCase().includes('duplicate') || msg.toLowerCase().includes('exists'))) this.renameError.set('A location with this name or code already exists');
                else this.renameError.set(typeof msg === 'string' ? msg : 'Failed to update location');
                this.renameSubmitting.set(false);
            });
    }
    cancelRename() {
        this.showRenameDialog.set(false);
    }

    // Move location actions
    confirmMoveLocation() {
        if (this.moveLocationSubmitting()) return;
        const target = this.moveLocationTarget();
        let newParentId = this.moveLocationNewParentId();

        // If moving to root, explicitly set parentId to null
        if (this.moveToRoot()) {
            newParentId = null;
        }

        if (!target || !target.data) {
            this.showMoveLocationDialog.set(false);
            return;
        }
        const locationData: any = target.data;
        const locationId = locationData.id;
        const namespaceId = locationData.appNamespaceId;
        const currentParentId = locationData.parentId || null;

        // Check if actually changing parent
        if (newParentId === currentParentId) {
            this.showMoveLocationDialog.set(false);
            return;
        }

        // Validate: cannot move to itself or its own descendants
        if (newParentId === locationId) {
            this.moveLocationError.set('Cannot move a location into itself');
            return;
        }

        // Check if newParentId is a descendant
        const allLocs = this.locations() || [];
        const isDescendant = (checkId: string | null, ancestorId: string): boolean => {
            if (!checkId) return false;
            if (checkId === ancestorId) return true;
            const loc = allLocs.find((l) => l.id === checkId);
            if (!loc || !loc.parentId) return false;
            return isDescendant(loc.parentId, ancestorId);
        };
        if (newParentId && isDescendant(newParentId, locationId)) {
            this.moveLocationError.set('Cannot move a location into one of its own descendants');
            return;
        }

        this.moveLocationSubmitting.set(true);
        this.moveLocationError.set(null);
        this.cloud
            .moveLocation(locationId, newParentId)
            .then(() => {
                if (namespaceId) this.cloud.refreshNamespaceLocations(namespaceId);
                this.showMoveLocationDialog.set(false);
                this.moveLocationSubmitting.set(false);
                setTimeout(() => this.trySelect(locationId), 500);
            })
            .catch((err: any) => {
                const msg = err?.error || err?.message || 'Failed to move location';
                this.moveLocationError.set(typeof msg === 'string' ? msg : 'Failed to move location');
                this.moveLocationSubmitting.set(false);
            });
    }
    cancelMoveLocation() {
        this.showMoveLocationDialog.set(false);
    }

    // Device creation actions
    openCreateDevice(locationNode: TreeNode) {
        this.createDeviceLocationTarget.set(locationNode);
        this.createDeviceLocationId.set(locationNode.key || null);
        this.createDeviceName.set('');
        this.createDeviceError.set(null);
        this.createDeviceResult.set(null);
        this.showCreateDeviceDialog.set(true);
    }
    cancelCreateDevice() {
        if (!this.createDeviceSubmitting()) this.showCreateDeviceDialog.set(false);
    }
    confirmCreateDevice() {
        if (this.createDeviceSubmitting()) return;
        const name = this.createDeviceName().trim();
        const locationNodeId = this.createDeviceLocationId();
        const locNode = this.createDeviceLocationTarget();
        if (!locNode || !name || !locationNodeId) {
            this.showCreateDeviceDialog.set(false);
            return;
        }
        const data: any = locNode.data;
        const namespaceId = data?.appNamespaceId;
        this.createDeviceSubmitting.set(true);
        this.createDeviceError.set(null);
        this.cloud
            .createDevice(name, namespaceId, locationNodeId)
            .then((res) => {
                this.createDeviceSubmitting.set(false);
                this.createDeviceResult.set(res);
            })
            .catch((err) => {
                const msg = err?.error || err?.message || 'Failed to create device';
                this.createDeviceError.set(typeof msg === 'string' ? msg : 'Failed to create device');
                this.createDeviceSubmitting.set(false);
            });
    }

    // Drag handle mouse tracking
    onHandleMouseDown(node: TreeNode) {
        this.handleMouseDownNode.set(node);
    }

    onHandleMouseUp() {
        this.handleMouseDownNode.set(null);
    }

    // Drag and drop methods using native HTML5 drag API
    onNativeDragStart(event: DragEvent, node: TreeNode) {
        // Only allow dragging if mouse was pressed on this node's handle
        const handleNode = this.handleMouseDownNode();
        if (!handleNode || handleNode.key !== node.key) {
            event.preventDefault();
            return;
        }

        // Check permission (every node is a location of the selected tenant)
        const namespaceId = (node.data as any)?.appNamespaceId;
        const adminRoles = [2];
        if (!this.cloud.hasNamespaceRole(namespaceId, adminRoles)) {
            event.preventDefault();
            return;
        }

        // Set drag data
        event.dataTransfer?.setData('text/plain', node.key || '');
        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
        }

        this.draggedNode.set(node);
        this.isDragging.set(true);
        this.handleMouseDownNode.set(null); // Clear the flag
    }

    onDragEnded() {
        this.draggedNode.set(null);
        this.dropTargetNode.set(null);
        this.isDragging.set(false);
    }

    onDragEnter(node: TreeNode) {
        const dragged = this.draggedNode();
        if (!dragged) return;

        // Can't drop on self
        if (node.key === dragged.key) {
            this.dropTargetNode.set(null);
            return;
        }

        // Check if target is a descendant of dragged node (would create circular reference)
        const allLocs = this.locations() || [];
        const isDescendant = (checkId: string | null, ancestorId: string): boolean => {
            if (!checkId) return false;
            if (checkId === ancestorId) return true;
            const loc = allLocs.find((l) => l.id === checkId);
            if (!loc || !loc.parentId) return false;
            return isDescendant(loc.parentId, ancestorId);
        };

        const draggedId = (dragged.data as any)?.id;
        const targetId = node.key;

        // Dropping on another location: check it's valid (moving to the root level is done via "Move Location")
        if (targetId && draggedId) {
            if (isDescendant(targetId, draggedId)) {
                this.dropTargetNode.set(null);
                return;
            }
            // Must be same namespace
            const draggedNs = (dragged.data as any)?.appNamespaceId;
            const targetNs = (node.data as any)?.appNamespaceId;
            if (draggedNs !== targetNs) {
                this.dropTargetNode.set(null);
                return;
            }
            this.dropTargetNode.set(node);
        }
    }

    onNativeDragLeave(event: DragEvent) {
        // Only clear if leaving the actual row, not entering a child element
        const relatedTarget = event.relatedTarget as HTMLElement;
        const currentTarget = event.currentTarget as HTMLElement;
        if (relatedTarget && currentTarget?.contains(relatedTarget)) {
            return;
        }
        this.dropTargetNode.set(null);
    }

    onNativeDragOver(event: DragEvent, node: TreeNode) {
        const dragged = this.draggedNode();
        if (!dragged) return;

        // Check if this is a valid drop target
        const isValid = this.dropTargetNode()?.key === node.key;
        if (isValid) {
            event.preventDefault();
            if (event.dataTransfer) {
                event.dataTransfer.dropEffect = 'move';
            }
        }
    }

    async onNativeDrop(event: DragEvent, targetNode: TreeNode) {
        event.preventDefault();

        const dragged = this.draggedNode();
        if (!dragged || !targetNode) {
            this.onDragEnded();
            return;
        }

        // Verify this is a valid drop target
        if (this.dropTargetNode()?.key !== targetNode.key) {
            this.onDragEnded();
            return;
        }

        const draggedData: any = dragged.data;
        const locationId = draggedData?.id;
        const namespaceId = draggedData?.appNamespaceId;

        if (!locationId || !namespaceId) {
            this.onDragEnded();
            return;
        }

        // Determine new parent ID (the drop target is always a location)
        const newParentId: string | null = targetNode.key || null;

        // Don't move if same parent
        const currentParentId = draggedData.parentId || null;
        if (newParentId === currentParentId) {
            this.onDragEnded();
            return;
        }

        // Perform the move
        try {
            await this.cloud.moveLocation(locationId, newParentId);
            this.cloud.refreshNamespaceLocations(namespaceId);
            setTimeout(() => this.trySelect(locationId), 500);
        } catch (err: any) {
            console.error('Failed to move location via drag-drop:', err);
        }

        this.onDragEnded();
    }

    canDrag(node: TreeNode): boolean {
        // Requires admin permission on the tenant
        const namespaceId = (node.data as any)?.appNamespaceId;
        if (!namespaceId) return false;
        const adminRoles = [2]; // Admin role
        return this.cloud.hasNamespaceRole(namespaceId, adminRoles);
    }

    isValidDropTarget(node: TreeNode): boolean {
        const target = this.dropTargetNode();
        return target?.key === node.key;
    }
}
